import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const CloseMenuContext = createContext(() => {});

const MENU_WIDTH = { sm: 160, md: 176, lg: 192 };

/**
 * Row action menu (⋮) portaled to document.body so it is never clipped by table overflow.
 */
export function ActionMenu({ children, size = 'sm', align = 'right' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuWidth = MENU_WIDTH[size] || MENU_WIDTH.sm;

  const close = useCallback(() => setOpen(false), []);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const menuHeight = menuRef.current?.offsetHeight || 160;

    let top = rect.bottom + gap;
    let left = align === 'right' ? rect.right - menuWidth : rect.left;

    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuHeight - gap);
    }
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));

    setPos({ top, left });
  }, [align, menuWidth]);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const raf = requestAnimationFrame(updatePosition);

    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    const onKey = e => e.key === 'Escape' && close();
    const onPointerDown = e => {
      if (
        !triggerRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        close();
      }
    };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, close, updatePosition]);

  const menu =
    open &&
    createPortal(
      <div
        ref={menuRef}
        role="menu"
        className="fixed bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] shadow-lg z-[200] py-1 rounded-xl overflow-hidden"
        style={{ top: pos.top, left: pos.left, width: menuWidth }}
        onClick={e => e.stopPropagation()}
      >
        <CloseMenuContext.Provider value={close}>{children}</CloseMenuContext.Provider>
      </div>,
      document.body
    );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="cursor-pointer text-[hsl(var(--ds-muted-fg))] px-1.5 py-0.5 hover:bg-[hsl(var(--ds-muted))] rounded-lg leading-none"
        aria-label="Actions"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={e => {
          e.stopPropagation();
          setOpen(v => !v);
        }}
      >
        ⋮
      </button>
      {menu}
    </>
  );
}

export function ActionMenuItem({ onClick, children, danger = false, disabled = false }) {
  const close = useContext(CloseMenuContext);
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      className={`block w-full text-left px-3 py-2 text-sm ${
        disabled ? 'text-[hsl(var(--ds-muted-fg))] cursor-not-allowed' : `hover:bg-[hsl(var(--ds-muted))] ${danger ? 'text-red-600' : ''}`
      }`}
      onClick={e => {
        if (disabled) return;
        e.stopPropagation();
        close();
        onClick?.(e);
      }}
    >
      {children}
    </button>
  );
}

/**
 * Generic dropdown with custom trigger (e.g. navbar user menu).
 */
export function DropdownMenu({ open, onOpenChange, trigger, children, size = 'lg', align = 'right' }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuWidth = MENU_WIDTH[size] || MENU_WIDTH.lg;

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const menuHeight = menuRef.current?.offsetHeight || 120;
    let top = rect.bottom + gap;
    let left = align === 'right' ? rect.right - menuWidth : rect.left;
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuHeight - gap);
    }
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    setPos({ top, left });
  }, [align, menuWidth]);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    const onKey = e => e.key === 'Escape' && close();
    const onPointerDown = e => {
      if (!triggerRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) {
        close();
      }
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, close, updatePosition]);

  const menu =
    open &&
    createPortal(
      <div
        ref={menuRef}
        className="fixed bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] rounded-xl shadow-lg z-[200] overflow-hidden"
        style={{ top: pos.top, left: pos.left, width: menuWidth }}
        onClick={e => e.stopPropagation()}
      >
        <CloseMenuContext.Provider value={close}>{children}</CloseMenuContext.Provider>
      </div>,
      document.body
    );

  return (
    <div className="inline-block" onClick={e => e.stopPropagation()}>
      <span ref={triggerRef}>{trigger}</span>
      {menu}
    </div>
  );
}
